import type {
  EmailContext,
  DraftResponse,
} from "../types";

const BASE_URL =
  import.meta.env.VITE_LIO_API_URL ?? "https://localhost:8000";

export interface Source {
  file: string;
  title: string;
}

interface SSECallbacks {
  onChunk: (content: string) => void;
  onDone: (conversationId?: string) => void;
  onError: (error: Error) => void;
  onSources?: (sources: Source[]) => void;
}

async function consumeSSE(
  response: Response,
  callbacks: SSECallbacks
): Promise<void> {
  if (!response.ok) {
    const text = await response.text().catch(() => "Unknown error");
    callbacks.onError(new Error(`API error ${response.status}: ${text}`));
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError(new Error("No response body"));
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let lastConversationId: string | undefined;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6).trim();
        if (data === "[DONE]") {
          callbacks.onDone(lastConversationId);
          return;
        }
        try {
          const parsed = JSON.parse(data);
          if ("error" in parsed) {
            callbacks.onError(new Error(parsed.error));
            return;
          }
          if (parsed.conversationId) {
            lastConversationId = parsed.conversationId;
          }
          if (parsed.sources && callbacks.onSources) {
            callbacks.onSources(parsed.sources);
          }
          if (parsed.content) {
            callbacks.onChunk(parsed.content);
          }
          if (parsed.done) {
            callbacks.onDone(lastConversationId);
            return;
          }
        } catch {
          // skip malformed lines
        }
      }
    }
    callbacks.onDone(lastConversationId);
  } catch (err) {
    callbacks.onError(
      err instanceof Error ? err : new Error("Stream read failed")
    );
  } finally {
    reader.releaseLock();
  }
}

export async function generateReply(
  context: EmailContext,
  token: string,
  callbacks: SSECallbacks,
  instruction?: string
): Promise<void> {
  const payload: Record<string, unknown> = { emailContext: context };
  if (instruction) {
    payload.instruction = instruction;
  }
  const response = await fetch(`${BASE_URL}/api/v1/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  await consumeSSE(response, callbacks);
}

export async function refineReply(
  conversationId: string,
  instruction: string,
  token: string,
  callbacks: SSECallbacks
): Promise<void> {
  const response = await fetch(`${BASE_URL}/api/v1/refine`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ conversationId, instruction }),
  });
  await consumeSSE(response, callbacks);
}

export async function createDraft(
  conversationId: string,
  content: string,
  token: string
): Promise<DraftResponse> {
  const response = await fetch(`${BASE_URL}/api/v1/draft`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ conversationId, content }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "Unknown error");
    throw new Error(`Draft creation failed (${response.status}): ${text}`);
  }
  return response.json();
}
